
'use client';

import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import type { Profile } from '@/lib/types';
import { useFirestore, useUser } from '@/firebase';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  setDoc,
  getDoc,
  where,
} from 'firebase/firestore';
import { errorEmitter } from '@/firebase/error-emitter';
import { logger } from '@/lib/logger';

interface ProfileContextType {
  profiles: Profile[];
  loading: boolean;
  currentProfile: Profile | null;
  setCurrentProfileById: (profileId: string | null) => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const firestore = useFirestore();
  const { user, loading: authLoading } = useUser();

  // 1. Create a stable query object using useMemo
  const profilesQuery = useMemo(() => {
    logger.debug('[Firestore Query] Attempting to build `profiles` query.');
    logger.debug('[Auth State]', user ? 'authenticated' : 'null');
    if (!firestore || !user?.uid) {
      logger.debug('[Firestore Query] SKIPPED `profiles` query: missing firestore or user.');
      return null;
    }
    logger.debug('[Firestore Query] Building profiles query for user');
    return query(
      collection(firestore, 'profiles'),
      where('ownerId', '==', user.uid),
      orderBy('createdAt', 'desc'),
    );
  }, [firestore, user?.uid]);

  // 2. useEffect for listening to profile changes, now depending on the stable query
  useEffect(() => {
    if (!profilesQuery) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = onSnapshot(
      profilesQuery,
      (snapshot) => {
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as any),
        }));
        
        setProfiles(items);


        setLoading(false);
      },
      (err) => {
        console.error('Failed to fetch profiles:', err);
        errorEmitter.emit('error', err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [profilesQuery]);

  // 4. useEffect for loading the last selected profile ID
  useEffect(() => {
    if (authLoading || !firestore || !user?.uid) return;

    const userSettingsRef = doc(firestore, 'users', user.uid);

    getDoc(userSettingsRef)
      .then((docSnap) => {
        const savedProfileId = docSnap.exists() ? docSnap.data()?.selectedProfileId : null;
        if (savedProfileId) {
            // 5. Only set state if the ID is different
            setSelectedProfileId(prevId => prevId === savedProfileId ? prevId : savedProfileId);
        }
      })
      .catch((err) => {
        console.warn('Could not load selected profile preference:', err);
      });
  }, [firestore, user, authLoading]);

  // 6. Make setCurrentProfileById stable with useCallback
  const setCurrentProfileById = useCallback(
    (profileId: string | null) => {
      setSelectedProfileId(profileId);

      if (firestore && user?.uid) {
        const userSettingsRef = doc(firestore, 'users', user.uid);
        setDoc(userSettingsRef, { selectedProfileId: profileId }, { merge: true })
          .catch((err) =>
            console.warn('Could not save profile selection to Firebase:', err),
          );
      }
    },
    [firestore, user],
  );

  useEffect(() => {
    if (authLoading || !user) return;
    if (profiles.length === 0) return;
  
    // Wenn noch nichts ausgewählt ist -> erstes Profil setzen
    if (!selectedProfileId) {
      setCurrentProfileById(profiles[0].id);
      return;
    }
  
    // Wenn gespeichertes Profil nicht mehr existiert -> fallback
    if (!profiles.some((p) => p.id === selectedProfileId)) {
      setCurrentProfileById(profiles[0].id);
    }
  }, [authLoading, user, profiles, selectedProfileId, setCurrentProfileById]);
  

  const currentProfile = useMemo(() => {
    if (!selectedProfileId || profiles.length === 0) {
      return null;
    }
    return profiles.find((p) => p.id === selectedProfileId) || null;
  }, [selectedProfileId, profiles]);

  return (
    <ProfileContext.Provider
      value={{
        profiles,
        loading: loading || authLoading,
        currentProfile,
        setCurrentProfileById,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}
